import { Component, effect, inject, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { NavbarComponent } from '../navbar/navbar.component';
import { IconComponent } from '../../shared/icons/icon.component';
import { SlidePanelComponent } from '../../shared/components/slide-panel/slide-panel.component';
import { AiChatPanelComponent } from '../../pages/public/chatbot/ai-chat-panel/ai-chat-panel.component';
import { ChatHistorySidebarComponent } from '../../pages/public/chatbot/chat-history-sidebar/chat-history-sidebar.component';
import { CommonService } from '../../core/services/common.service';
import { GlobalLoaderComponent } from '../../shared/components/loader-new/loader.component';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MobileNavSheetComponent } from '../mobile-nav-sheet/mobile-nav-sheet.component';
import { MobileSheetComponent, MobileSheetData } from '../../shared/components/mobile-sheet/mobile-sheet.component';
import { DropdownOption } from '../../shared/components/ui/dropdown/dropdown.component';
import { ProductSelectionListComponent } from '../product-selection-list/product-selection-list.component';
import { IconComponentV2 } from '../../shared/components/icon-component/icon.component';
import { ApiService } from '../../core/services/api.service';
import { FormsModule } from '@angular/forms';
// import { Router } from '@angular/router';
import { BreakpointService } from '../../core/services/breakpoint.service';
import { ThemeToggleComponent } from '../../shared/components/theme-toggle/theme-toggle.component';
import { AgentRequestInboxComponent } from '../../shared/components/agent-request-inbox/agent-request-inbox.component';

@Component({
  selector: 'app-private-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    SidebarComponent,
    NavbarComponent,
    IconComponent,
    SlidePanelComponent,
    AiChatPanelComponent,
    GlobalLoaderComponent,
    ChatHistorySidebarComponent,
    MatBottomSheetModule,
    FormsModule,
    MobileNavSheetComponent,
    IconComponentV2,
    ThemeToggleComponent,
    AgentRequestInboxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div id="private_layout" class="h-dvh w-full relative flex bg-transparent overflow-hidden"
         [style.--sidebar-width]="sidebarRef?.isCollapsed() ? '5rem' : '16rem'"
         [style.--navbar-height]="'4rem'"> 
      @if (_breakpointService.isDesktop()) {
        <!-- Decorative colour blobs are light-mode only: the dark theme is a flat matte canvas -->
        <div class="fixed inset-0 overflow-hidden pointer-events-none dark:hidden" aria-hidden="true">
          <div class="absolute w-[600px] h-[600px] rounded-full blur-[130px] top-[5%] left-[10%] bg-purple-400/15 dark:bg-purple-600/25 will-change-transform [transform:translateZ(0)]"></div>
          <div class="absolute w-[500px] h-[500px] rounded-full blur-[110px] top-[60%] right-[5%] bg-indigo-400/12 dark:bg-indigo-600/20 will-change-transform [transform:translateZ(0)]"></div>
          <div class="absolute w-[450px] h-[450px] rounded-full blur-[120px] bottom-[10%] left-[15%] bg-violet-400/12 dark:bg-violet-600/20 will-change-transform [transform:translateZ(0)]"></div>
          <div class="absolute w-[400px] h-[400px] rounded-full blur-[100px] top-[30%] right-[20%] bg-fuchsia-400/10 dark:bg-fuchsia-600/15 will-change-transform [transform:translateZ(0)]"></div>
          <div class="absolute w-[350px] h-[350px] rounded-full blur-[90px] bottom-[15%] right-[30%] bg-blue-400/12 dark:bg-blue-600/20 will-change-transform [transform:translateZ(0)]"></div>
        </div>
      }


      <!-- Sidebar - Full height on left -->
      <div [class.hidden]="!_breakpointService.isDesktop()">
        <app-sidebar #sidebarRef />
      </div>

      <button
        (click)="sidebarRef.toggleCollapse()"
        [class.hidden]="!_breakpointService.isDesktop()"
        class="flex absolute cursor-pointer top-[3.3rem] w-5 h-5 rounded-full border items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 z-[5000] shadow-md border-[#EAEAEA] text-white shadow-purple-500/30 bg-white/80"
        [ngClass]="{
          'left-[4.375rem]': $safeNavigationMigration(sidebarRef?.isCollapsed()),
          'left-[15.375rem]': !sidebarRef?.isCollapsed(),
          'dark:shadow-none dark:border-line dark:bg-surface-1/90': !_breakpointService.isMobile()
        }"
      >
        <app-icon
          [name]="sidebarRef?.isCollapsed() ? 'chevronsRight' : 'chevronsLeft'"
          [size]="16"
          class="flex-shrink-0 text-purple-500 z-10"
          [ngClass]="{ 'dark:text-purple-300': !_breakpointService.isMobile() }"
        />
      </button>

      <!-- Mobile Bottom Navigation -->
      <div [class.hidden]="_breakpointService.isDesktop()" [ngClass]="{ 'dark:bg-surface-1': !_breakpointService.isMobile() }">
        <app-mobile-nav-sheet mode="bar" />
      </div>

      <!-- Right side: Navbar + Content -->
      <div class="flex-1 flex flex-col min-h-0">
        <!-- Navbar at top -->
        <div [class.hidden]="!_breakpointService.isDesktop()">
          <app-navbar />
        </div>

        <!-- Mobile Header -->
        @if (!_commonService.isDetailedViewActive()) {
           
          <div [class.hidden]="_breakpointService.isDesktop()" class="flex items-center justify-between gap-3 px-4 pt-8 pb-5 bg-white dark:bg-surface-1 border-sky-100 dark:border-line sticky top-0 z-[5500]">
            <!-- Product Selector (Bottom Sheet on Mobile) -->
            <div class="w-full max-w-[200px]">
            <!-- <app-dropdown
              [options]="productsList()"
              [selectedValue]="selectedProductId()"
              (selectionChange)="onProductChange($event)"
              [placeholder]="'Select Product'"
              variant="themeBase"
              size="md"
              [fullWidth]="true"
              triggerClass="!bg-white dark:!bg-white/5 !border-gray-100 dark:!border-white/10 !rounded-xl !shadow-sm transition-all active:scale-95 flex items-center justify-between !px-4 !py-2 !h-auto !min-h-0"
              textClass="!text-[10px] !font-bold !text-[#141437] dark:!text-white !uppercase !tracking-wider"
              iconClass="!text-gray-400"
              chevronIcon="chevronDown"
              [triggerIconSize]="14"
              [useImageForIcon]="true"
            ></app-dropdown> -->

            <button (click)="openProductSheet()" 
                    class="w-full bg-white dark:bg-surface-1 border border-sky-100 dark:border-line rounded-xl shadow-sm transition-all active:scale-95 flex items-center justify-between px-4 py-2 h-auto min-h-0 group">
              <div class="flex items-center gap-2 overflow-hidden">
                @let selected = getSelectedProduct();
                @if (selected?.icon) {
                  <img [src]="$safeNavigationMigration(selected?.icon)" class="w-5 h-5 rounded-md object-cover" alt="product icon" />
                } @else {
                  <app-icon-v2 name="layoutDashboard" [size]="16" class="text-gray-400"></app-icon-v2>
                }
                <span class="text-[10px] font-bold text-[#141437] dark:text-white uppercase tracking-wider truncate">
                  {{ selected?.label || 'Select Product' }}
                </span>
              </div>
              <app-icon-v2 name="chevronDown" [size]="14" class="text-gray-400 group-hover:text-purple-500 transition-colors"></app-icon-v2>
            </button>
          </div>

          <!-- Theme Toggle (Mobile only) -->
          <app-theme-toggle
            [variant]="'button'"
            [buttonClass]="'border border-sky-100 dark:border-line rounded-xl shadow-sm shrink-0'"
          ></app-theme-toggle>
        </div>
        }

        <!-- Main Content (scrolls only here) -->
        <div (scroll)="onMainScroll($event)" class="layout-main-scroll relative grid grid-cols-12 overflow-y-auto h-full">
          <app-global-loader />
           <div (scroll)="onMainScroll($event)"
               class="w-full xl:px-6 xl:py-4 p-0 overflow-y-auto overflow-x-hidden flex-1"
               [ngClass]="{
                 'col-span-12': !panelOpen(),
                 'col-span-8': panelOpen(),
                 'bg-white dark:bg-surface-1': !_breakpointService.isDesktop(),
                 'bg-transparent': _breakpointService.isDesktop()
               }">
              <!--
            @if (!isMyDashboardRoute()) {
              <ai-headlines class="block"></ai-headlines>
            } -->
            <router-outlet></router-outlet>
          </div>
          @if (panelOpen()) {
            <div class="col-span-4 max-h-[calc(100dvh-96px)] sticky top-4 mt-4">
              <app-slide-panel
                [open]="panelOpen()"
                (closed)="closePanel()"
                  title="Ask Prism AI"
                  titleIcon="sparkles"
                  [showClose]="true"
                  [showHistoryIcon]="true"
                  (historyClicked)="toggleHistory()"
                  mode="inline"
                  containerClass="w-full rounded-xl"
                  [panelClass]="'w-full rounded-xl bg-white border border-sky-200 shadow-xl overflow-hidden ' + (!_breakpointService.isMobile() ? 'dark:bg-surface-1 dark:border-slate-800' : '')"
                  [headerClass]="'bg-white border-sky-200 text-slate-700 ' + (!_breakpointService.isMobile() ? 'dark:bg-surface-1 dark:border-slate-800 dark:text-slate-200' : '')"
                  contentClass="!p-0 relative"
                >
                @if (isHistoryOpen()) {
                  <div class="absolute inset-0 z-50 bg-white" [ngClass]="{ 'dark:bg-surface-1': !_breakpointService.isMobile() }">
                    <app-chat-history-sidebar (sessionSelected)="onSessionSelected($event)"></app-chat-history-sidebar>
                  </div>
                }
                <app-ai-chat-panel #aiChatPanel [suggestions]="_commonService.aiSuggestions()" [chatType]="_commonService.chatType()"></app-ai-chat-panel>

              </app-slide-panel>
            </div>
          }
        </div>
      </div>

      <!-- "A live demo needs an agent" — a fixed overlay, so it finds the user on any page.
           It renders nothing at all unless this user is ONLINE and a demo is ringing them. -->
      <app-agent-request-inbox />
    </div>
  `,
})
export class PrivateLayoutComponent {
  @ViewChild('sidebarRef') sidebarComponent!: SidebarComponent;
  @ViewChild('aiChatPanel') aiChatPanel!: AiChatPanelComponent;
  panelOpen = signal(false);
  isHistoryOpen = signal(false);
  productsList = signal<DropdownOption[]>([]);
  selectedProductId = signal<string | null>(null);

  // public _commonService = inject(CommonService);
  private _bottomSheet = inject(MatBottomSheet);
  private _apiService = inject(ApiService);
  private _router = inject(Router);
  public _breakpointService = inject(BreakpointService);
  protected _commonService = inject(CommonService);
  // private _router = inject(Router);

  /**
   * True when the active route is the My Dashboard page.
   *
   * Why this exists: `<ai-headlines>` (rendered globally inside this layout)
   * runs an `effect()` on `apiService.selectedProductId()` and fires
   * `GET summary/headlines/latest?productId=<id>` every time the product
   * changes. The My Dashboard page already pins `selectedProductId` to a
   * numeric value during `loadCatalogData()` to keep the embedded MR1/MR2
   * children sending a usable `product_id`, which would otherwise cause an
   * extra unwanted `summary/headlines/latest` request on this route. By
   * gating the `<ai-headlines>` render on this signal, the headlines
   * component is never instantiated on `/my-dashboard` so its effect — and
   * therefore the API call — never runs there.
   *
   * Updated on every `NavigationEnd` so SPA route changes flip it without
   * a page reload. Seeded from the current `Router.url` so the first
   * paint after a hard refresh on `/my-dashboard` is correct too.
   */
  readonly isMyDashboardRoute = signal(false);

  constructor() {
    effect(() => {
      this.panelOpen.set(this._commonService.isPanelOpen());
    });

    this.loadProducts();
  }

  loadProducts(): void {
    this._apiService.fetchProducts().subscribe({
      next: (response: any) => {
        const stored = sessionStorage.getItem('dynamic_routes');
        const dynamicRoutes = stored ? JSON.parse(stored) : [];

        const allowedProductIds = dynamicRoutes
          .map((item: any) => item.product?.id)
          .filter(Boolean);

        const filteredProducts = response
          .filter(
            (item: any) => item.active && allowedProductIds.includes(item.id),
          )
          .map((item: any) => ({
            value: item.id,
            label: item.name,
            icon: item.icon
          }));

        this.productsList.set(filteredProducts);

        // Set initial product
        const currentId = this._apiService.getSelectedProductId();
        if (currentId) {
          this.selectedProductId.set(currentId);
        } else {
          let productIdFromUrl: string | null = null;
          if (typeof window !== 'undefined') {
            const currentUrl = window.location.pathname;
            const urlMatch = currentUrl.match(/^\/app\/([^\/]+)/);
            if (urlMatch) {
              const productBasePath = urlMatch[1];
              const matchedProduct = dynamicRoutes.find((p: any) => {
                const basePath = p.product?.basePath;
                if (!basePath) return false;
                const cleanBasePath = basePath.split('/').filter(Boolean).pop();
                return cleanBasePath === productBasePath;
              });
              if (matchedProduct) {
                productIdFromUrl = matchedProduct.product?.id || null;
              }
            }
          }

          if (productIdFromUrl) {
            this.selectedProductId.set(productIdFromUrl);
            this._apiService.setSelectedProductId(productIdFromUrl);
          } else {
            const denefits = filteredProducts.find(
              (p: { label: string }) => p.label.toLowerCase() === 'denefits',
            );
            if (denefits) {
              this.selectedProductId.set(denefits.value);
              this._apiService.setSelectedProductId(denefits.value);
            } else if (filteredProducts.length > 0) {
              this.selectedProductId.set(filteredProducts[0].value);
              this._apiService.setSelectedProductId(filteredProducts[0].value);
            }
          }
        }
      },
      error: (error: any) => console.error('Error fetching products:', error),
    });
  }

  onProductChange(productId: string) {
    if (!productId) return;
    this.selectedProductId.set(productId);
    this._apiService.setSelectedProductId(productId);

    // Navigate to first route of the product (copied logic from sidebar)
    const stored = sessionStorage.getItem('dynamic_routes');
    if (stored) {
      const allProducts = JSON.parse(stored);
      const selectedProduct = allProducts.find((p: any) => p.product?.id === productId);

      if (selectedProduct?.routes?.length) {
        const toAppRoute = (path: string): string => {
          if (!path) return '/app';
          return path.startsWith('/app') ? path : '/app/' + path.replace(/^\/+/, '');
        };

        const firstRoute = selectedProduct.routes[0];
        const targetRoute = firstRoute.subMenus?.length
          ? toAppRoute(firstRoute.subMenus[0].path)
          : toAppRoute(firstRoute.path);

        if (targetRoute) {
          this._router.navigateByUrl(targetRoute);
        }
      }
    }
  }

  getSelectedProduct() {
    return this.productsList().find(p => p.value === this.selectedProductId());
  }

  openProductSheet() {
    const sheetRef = this._bottomSheet.open(MobileSheetComponent, {
      data: {
        title: 'Select Product',
        component: ProductSelectionListComponent,
        data: {
          products: this.productsList(),
          selectedId: this.selectedProductId()
        }
      },
      panelClass: 'mobile-sheet-wrapper-container'
    });

    sheetRef.afterDismissed().subscribe((result: string) => {
      if (result) {
        this.onProductChange(result);
      }
    });
    this.isMyDashboardRoute.set(this._isMyDashboardUrl(this._router.url));
    this._router.events
      .pipe(
        filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((evt) => {
        this.isMyDashboardRoute.set(this._isMyDashboardUrl(evt.urlAfterRedirects ?? evt.url));
      });
  }

  /** Match `/my-dashboard` anywhere in the path so any wrapping segment
   *  (e.g. `/app/dashboard/my-dashboard`) is detected. */
  private _isMyDashboardUrl(url: string): boolean {
    return (url || '').toLowerCase().includes('my-dashboard');
  }

  closePanel() {
    this.panelOpen.set(false);
    this.isHistoryOpen.set(false);
    this._commonService.isPanelOpen.set(this.panelOpen());
  }

  toggleHistory() {
    this.isHistoryOpen.set(!this.isHistoryOpen());
  }

  onSessionSelected(session: any) {
    this.isHistoryOpen.set(false);
    if (this.aiChatPanel) {
      this.aiChatPanel.loadChatSession(session);
    }
  }
 
  onMainScroll(event: any) {
    const scrollTop = Math.max(event?.target?.scrollTop ?? 0, 0);
    const atTop = scrollTop <= 0;

    if (!this._commonService.forceHideMobileNav()) {
      this._commonService.showMobileNav.set(atTop);
    }
 
    if (!this._commonService.forceHideMobileFloatingBar()) {
      this._commonService.showMobileFloatingBar.set(true);
    }
  }
}
